/**
 * UI アニメーション中フラグ。 CPU 思考起動を「全アニメ完了」 までゲートするのに使う。
 *
 * 設計:
 *  - main thread は 1 つなので、 「どの盤の」 アニメかではなく global counter で十分。
 *  - 各 アニメ対象要素は `useUiBusyAnim(ref)` hook を付けると、 animationstart で +1、
 *    animationend / cancel / 要素 unmount で -1 する。 React の synthetic event だと
 *    animationcancel が無い + unmount で end を取り損ねる ので addEventListener で扱う。
 *  - CPU 起動側は `awaitIdle()` で「現在アニメ中」 が 0 になるまで待つ。
 *  - 非アニメ盤 (Chess / Renju / Shogi / Jungo / AnimalShogi / Backgammon) は誰も
 *    `beginAnimation` を呼ばないので、 `awaitIdle` は raf x2 後に即 resolve する。
 *  - 暴走防止: `maxMs` (既定 400ms) で必ず resolve する hard timeout 付き。
 *
 * 使い方:
 *
 * ```tsx
 * function Stone() {
 *   const ref = useRef<HTMLSpanElement>(null);
 *   useUiBusyAnim(ref);
 *   return <span ref={ref} className="stone" />;
 * }
 *
 * // CPU 起動側
 * await Promise.all([sleep(minDelay), awaitIdle()]);
 * ```
 */

import { type RefObject, useEffect } from 'react';

let activeAnimations = 0;
const resolvers: (() => void)[] = [];

export function beginAnimation(): void {
  activeAnimations += 1;
}

export function endAnimation(): void {
  activeAnimations = Math.max(0, activeAnimations - 1);
  if (activeAnimations === 0) {
    const list = resolvers.splice(0);
    for (const r of list) r();
  }
}

/** テスト用。 counter / resolver を全部リセット。 production では使わない。 */
export function __resetUiBusy(): void {
  activeAnimations = 0;
  resolvers.splice(0);
}

/** 現在アニメ中かどうか。 デバッグ・テスト用。 */
export function isBusy(): boolean {
  return activeAnimations > 0;
}

/**
 * 「アニメ中フラグが 0」 になるまで待つ。
 *
 * - 既に 0 のときは、 これから始まるアニメを捕まえるため `requestAnimationFrame` を
 *   2 回挟んで resolve する (= mount 直後の onAnimationStart を待つ)。
 * - 0 でないときは end の通知を待つ。
 * - **`maxMs` は強制 resolve のハードタイムアウト** (= 安全弁)。 タブが background
 *   に行くと `requestAnimationFrame` は throttle (頻度激減 〜 完全停止) されるため、
 *   rAF だけに頼ると hidden tab で停止する。 CPU 思考の起動を blocking する hook
 *   なので、 必ず `maxMs` 以内に resolve することを保証する (Promise.race)。
 */
export function awaitIdle(maxMs = 400): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    // ハードタイムアウト: rAF 経路が hidden tab で stall するケースに備える。
    // setTimeout も hidden tab で 1Hz 程度に throttle されるが、 rAF より確実に発火する。
    setTimeout(finish, maxMs);

    if (activeAnimations === 0) {
      // mount 直後の animationstart を取りこぼさないため raf x2 を挟む。
      // bun test など raf 無し環境は setTimeout(0) に fallback。
      nextFrame(() => {
        if (done) return;
        nextFrame(() => {
          if (done) return;
          if (activeAnimations === 0) {
            finish();
            return;
          }
          waitForIdle(finish, maxMs);
        });
      });
      return;
    }
    waitForIdle(finish, maxMs);
  });
}

function nextFrame(cb: () => void): void {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(cb);
  } else {
    setTimeout(cb, 0);
  }
}

function waitForIdle(resolve: () => void, maxMs: number): void {
  let done = false;
  const r = () => {
    if (done) return;
    done = true;
    resolve();
  };
  resolvers.push(r);
  setTimeout(() => {
    if (done) return;
    done = true;
    const i = resolvers.indexOf(r);
    if (i >= 0) resolvers.splice(i, 1);
    resolve();
  }, maxMs);
}

/**
 * アニメ対象要素の ref に animationstart/end/cancel を購読させる hook。
 * mount 後に start が来たら counter++、 end/cancel/unmount で counter-- する。
 * 同じ要素で複数アニメが連続発火しても、 start ごとに +1 / end ごとに -1 で釣り合う。
 */
export function useUiBusyAnim<T extends HTMLElement>(ref: RefObject<T | null>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let started = 0; // この要素で counter にどれだけ +1 したか (= unmount で 0 に戻す)
    const onStart = () => {
      started += 1;
      beginAnimation();
    };
    const onEnd = () => {
      if (started > 0) {
        started -= 1;
        endAnimation();
      }
    };
    el.addEventListener('animationstart', onStart);
    el.addEventListener('animationend', onEnd);
    el.addEventListener('animationcancel', onEnd);
    return () => {
      el.removeEventListener('animationstart', onStart);
      el.removeEventListener('animationend', onEnd);
      el.removeEventListener('animationcancel', onEnd);
      // unmount 時点で未終了の start ぶんを巻き戻す (= 走り中アニメは canceled 扱い)
      while (started > 0) {
        started -= 1;
        endAnimation();
      }
    };
  }, [ref]);
}
